import cls from "classnames"
import { useEffect, useMemo, useState } from "react"

import { getDrawInfo, type DrawInfo } from "@/services/core"
import StrokeText from "@/components/StrokeText"
import { i18n } from "@/i18n"
import { useRoleInfo } from "@/user/session"
import { RECALL_REWARD_MAP } from "../../const/reward"

import styles from "./index.module.scss"

const getRewardFontSize = (fullText: string): number => {
    const len = fullText.length
    if (len <= 12) return 29
    if (len <= 20) return 26
    if (len <= 30) return 23
    return 20
}

const splitRewardText = (text: string): string[] => {
    const words = text.split(" ")
    const lines: string[] = []
    let currentLine: string[] = []

    for (const word of words) {
        currentLine.push(word)
        const lineText = currentLine.join(" ")
        if (lineText.length > 12) {
            if (currentLine.length > 1) {
                currentLine.pop()
                lines.push(currentLine.join(" "))
                currentLine = [word]
            } else {
                lines.push(lineText)
                currentLine = []
            }
        }
    }
    if (currentLine.length > 0) {
        lines.push(currentLine.join(" "))
    }
    return lines
}

export const Reward: React.FC = () => {
    const [drawInfo, setDrawInfo] = useState<DrawInfo | null>(null)
    const user = useRoleInfo()

    useEffect(() => {
        if (!user) return
        getDrawInfo().then((res) => {
            if (res.code === 0) {
                setDrawInfo(res.data)
            }
        })
    }, [user])

    const rewardList = useMemo(() => {
        if (!drawInfo?.coupons) {
            return []
        }
        return drawInfo.coupons.filter(
            (q) =>
                !!q.result &&
                q.result?.giftName &&
                q.cid === __RECALL_COUPON_CID,
        )
    }, [drawInfo])

    return (
        <div className={styles.reward}>
            {rewardList.length ? (
                <div className={styles.list}>
                    {rewardList.map((q, index) => {
                        const reward = RECALL_REWARD_MAP[q.result!.giftName]
                        if (!reward) {
                            return null
                        }

                        const { src, title: name, amount } = reward
                        const fullText = `${name}x${amount}`
                        const fontSize = getRewardFontSize(fullText)

                        if (fontSize < 26) {
                            const lines = splitRewardText(name)
                            return (
                                <div key={index} className={styles.reward}>
                                    <div
                                        className={styles.icon}
                                        style={{
                                            backgroundImage: `url(${src})`,
                                        }}
                                    />
                                    {lines.map((line, lineIndex) => (
                                        <div
                                            key={lineIndex}
                                            className={styles.line}
                                        >
                                            <StrokeText
                                                text={line}
                                                fontFamily="Georgia-Bold"
                                                fontSize={fontSize}
                                                fillColor="#8f7254"
                                                borderWidth={3}
                                                borderColor="#fffbf7"
                                                offsetY={0}
                                                padding={3}
                                                baselineAligned
                                            />
                                        </div>
                                    ))}
                                    <div className={styles.line}>
                                        <StrokeText
                                            text={`x${amount}`}
                                            fontFamily="Georgia-Bold"
                                            fontSize={fontSize}
                                            fillColor="#8f7254"
                                            borderWidth={3}
                                            borderColor="#fffbf7"
                                            offsetY={0}
                                            padding={3}
                                            baselineAligned
                                        />
                                    </div>
                                </div>
                            )
                        }

                        return (
                            <div key={index} className={styles.reward}>
                                <div
                                    className={styles.icon}
                                    style={{ backgroundImage: `url(${src})` }}
                                />
                                <div className={styles.line}>
                                    <StrokeText
                                        text={name}
                                        fontFamily="Georgia-Bold"
                                        fontSize={fontSize}
                                        fillColor="#8f7254"
                                        borderWidth={3}
                                        borderColor="#fffbf7"
                                        offsetY={0}
                                        padding={3}
                                        baselineAligned
                                    />
                                </div>
                                <div className={styles.line}>
                                    <StrokeText
                                        text={`x${amount}`}
                                        fontFamily="Georgia-Bold"
                                        fontSize={fontSize}
                                        fillColor="#8f7254"
                                        borderWidth={3}
                                        borderColor="#fffbf7"
                                        offsetY={0}
                                        padding={3}
                                        baselineAligned
                                    />
                                </div>
                            </div>
                        )
                    })}
                </div>
            ) : (
                <div className={cls(styles.tips, styles.small)}>
                    <div className={styles.arrow} />
                    <div className={styles.text}>
                        <StrokeText
                            text={i18n.getRecall("record.empty")}
                            fontFamily="Georgia-Bold"
                            fontSize={36}
                            fillColor="#fff9ed"
                            borderWidth={3}
                            borderColor="#614518"
                            offsetY={0}
                            padding={3}
                            baselineAligned
                        />
                    </div>
                    <div className={styles.arrow} />
                </div>
            )}
        </div>
    )
}
